// Health & Safety (Phase C) — role-adaptive. Anyone reports + tracks incidents;
// HR/safety triages, investigates and records corrective actions.
import { useState, useEffect } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, TextField } from '@mui/material';
import { Plus, HardHat, ShieldAlert, Trash2, Leaf } from 'lucide-react';
import { Card, SectionTitle, CenterSpinner, PrimaryBtn, OutlineBtn, Pill } from './ui';
import { EmptyState } from './components';
import { AG } from './theme/tokens';
import { api } from './api';
import { useAuth } from './auth';

const TYPES = ['Injury', 'NearMiss', 'Hazard', 'Illness', 'Other'];
const SEVERITIES = ['Low', 'Medium', 'High', 'Critical'];
const NEXT = { Open: ['Investigating', 'Resolved', 'Closed'], Investigating: ['Resolved', 'Closed'], Resolved: ['Closed'], Closed: [] };
const sevTone = (s) => (s === 'Critical' ? 'error' : s === 'High' ? 'warning' : s === 'Medium' ? 'info' : 'neutral');
const statTone = (s) => (s === 'Resolved' ? 'success' : s === 'Investigating' ? 'info' : s === 'Closed' ? 'neutral' : 'warning');
const CLAIM_TYPES = ['Medical', 'Compensation', 'Disability', 'Other'];
const CLAIM_NEXT = { Submitted: ['UnderReview', 'Approved', 'Rejected'], UnderReview: ['Approved', 'Rejected'], Approved: ['Settled'], Rejected: [], Settled: [] };
const claimTone = (s) => (s === 'Approved' || s === 'Settled' ? 'success' : s === 'Rejected' ? 'error' : s === 'UnderReview' ? 'info' : 'warning');
const money = (n) => `Rs ${Number(n || 0).toLocaleString('en-IN')}`;
const tabBtn = (active) => ({ padding: '8px 14px', borderRadius: 10, border: `1px solid ${active ? AG.primary : AG.n200}`, background: active ? AG.primaryLightest : '#fff', color: active ? AG.primaryDark : AG.n600, fontWeight: 600, fontSize: 13, cursor: 'pointer', fontFamily: 'inherit' });

export default function Safety({ showToast }) {
  const { isHR } = useAuth();
  return isHR ? <SafetyHR showToast={showToast} /> : <SafetyEmployee showToast={showToast} />;
}

function SafetyEmployee({ showToast }) {
  const [rows, setRows] = useState(null);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [claims, setClaims] = useState(null);
  const [claimForm, setClaimForm] = useState(null);
  const [rehab, setRehab] = useState([]);
  const load = () => api.myIncidents().then(setRows).catch(() => setRows([]));
  const loadClaims = () => api.myClaims().then(setClaims).catch(() => setClaims([]));
  useEffect(() => { load(); loadClaims(); api.myRehab().then(setRehab).catch(() => setRehab([])); }, []);
  const submit = async () => {
    if (!form.title.trim() || !form.description.trim() || busy) return; setBusy(true);
    try { await api.reportIncident(form); showToast?.('Incident reported'); setForm(null); load(); }
    catch (e) { showToast?.(e.message); } finally { setBusy(false); }
  };
  const fileClaim = async () => {
    if (!claimForm.description.trim() || busy) return; setBusy(true);
    try { await api.fileClaim({ type: claimForm.type, description: claimForm.description, amountClaimed: Number(claimForm.amountClaimed) || 0 }); showToast?.('Claim filed'); setClaimForm(null); loadClaims(); }
    catch (e) { showToast?.(e.message); } finally { setBusy(false); }
  };
  if (!rows) return <CenterSpinner />;
  return (
    <div className="space-y-4 fade-up">
      <div className="flex items-center justify-between" style={{ gap: 12, flexWrap: 'wrap' }}>
        <SectionTitle title="Health & Safety" sub="Report a hazard, near-miss or injury" />
        <PrimaryBtn onClick={() => setForm({ type: 'Hazard', severity: 'Low', title: '', description: '', location: '', occurredAt: '' })}><Plus size={14} style={{ marginRight: 6 }} /> Report incident</PrimaryBtn>
      </div>
      {rows.length === 0 ? <Card><EmptyState icon={HardHat} title="No reports" desc="Report hazards or incidents to keep everyone safe." /></Card> : (
        <div style={{ display: 'grid', gap: 8 }}>
          {rows.map((i) => (
            <Card key={i.id} style={{ padding: 14 }}>
              <div className="flex items-center justify-between">
                <div>
                  <div style={{ fontWeight: 600, color: AG.text }}>{i.title} <span style={{ color: AG.n400, fontWeight: 400 }}>· {i.type}</span></div>
                  <div style={{ fontSize: 12, color: AG.n500 }}>{i.occurredAt || i.createdAt?.slice(0, 10)}{i.location ? ` · ${i.location}` : ''}</div>
                </div>
                <div className="flex items-center gap-2"><Pill tone={sevTone(i.severity)}>{i.severity}</Pill><Pill tone={statTone(i.status)} dot>{i.status}</Pill></div>
              </div>
              {i.correctiveAction && <div style={{ fontSize: 13, color: AG.n600, marginTop: 8, borderTop: `1px solid ${AG.divider}`, paddingTop: 8 }}><b style={{ color: AG.n600 }}>Action:</b> {i.correctiveAction}</div>}
            </Card>
          ))}
        </div>
      )}
      <ReportDialog form={form} setForm={setForm} onSubmit={submit} busy={busy} />

      {/* My claims (6) */}
      <div className="flex items-center justify-between" style={{ gap: 12, flexWrap: 'wrap', marginTop: 8 }}>
        <SectionTitle title="My claims" sub="Compensation & medical claims" />
        <PrimaryBtn onClick={() => setClaimForm({ type: 'Medical', description: '', amountClaimed: '' })}><Plus size={14} style={{ marginRight: 6 }} /> File a claim</PrimaryBtn>
      </div>
      {!claims ? <CenterSpinner /> : claims.length === 0 ? (
        <Card><EmptyState icon={ShieldAlert} title="No claims" desc="File a compensation or medical claim and track its status here." /></Card>
      ) : (
        <div style={{ display: 'grid', gap: 8 }}>
          {claims.map((c) => (
            <Card key={c.id} style={{ padding: 14 }}>
              <div className="flex items-center justify-between">
                <div><div style={{ fontWeight: 600, color: AG.text }}>{c.type} <span style={{ color: AG.n400, fontWeight: 400 }}>· {money(c.amountClaimed)}</span></div><div style={{ fontSize: 12.5, color: AG.n500, marginTop: 2 }}>{c.description}</div>{c.amountApproved ? <div style={{ fontSize: 12, color: AG.successDark, marginTop: 2 }}>Approved: {money(c.amountApproved)}</div> : null}</div>
                <Pill tone={claimTone(c.status)} dot>{c.status}</Pill>
              </div>
            </Card>
          ))}
        </div>
      )}
      {/* My return-to-work plans (read-only) */}
      {rehab.length > 0 && (
        <>
          <SectionTitle title="My return-to-work plan" sub="Your rehabilitation plan from HR" />
          <div style={{ display: 'grid', gap: 8 }}>
            {rehab.map((r) => (
              <Card key={r.id} style={{ padding: 14 }}>
                <div className="flex items-center justify-between"><div style={{ fontWeight: 600, color: AG.text }}>{r.summary}</div><Pill tone={r.status === 'Completed' ? 'success' : r.status === 'OnHold' ? 'warning' : 'info'} dot>{r.status}</Pill></div>
                {r.restrictions && <div style={{ fontSize: 12.5, color: AG.n500, marginTop: 4 }}>Restrictions: {r.restrictions}</div>}
                <div style={{ fontSize: 12, color: AG.n400, marginTop: 2 }}>{r.startDate || '—'} → {r.actualReturnDate || r.expectedReturnDate || '—'}</div>
              </Card>
            ))}
          </div>
        </>
      )}

      <Dialog open={!!claimForm} onClose={() => !busy && setClaimForm(null)} maxWidth="xs" fullWidth>
        <DialogTitle>File a claim</DialogTitle>
        <DialogContent>
          {claimForm && (
            <div style={{ paddingTop: 6, display: 'grid', gap: 12 }}>
              <label><span style={{ display: 'block', fontSize: 12, fontWeight: 600, color: AG.n600, marginBottom: 4 }}>Type</span><select className="ag-input" value={claimForm.type} onChange={(e) => setClaimForm({ ...claimForm, type: e.target.value })}>{CLAIM_TYPES.map((t) => <option key={t}>{t}</option>)}</select></label>
              <TextField size="small" label="Describe the claim" multiline minRows={3} value={claimForm.description} onChange={(e) => setClaimForm({ ...claimForm, description: e.target.value })} />
              <TextField size="small" type="number" label="Amount claimed (Rs)" value={claimForm.amountClaimed} onChange={(e) => setClaimForm({ ...claimForm, amountClaimed: e.target.value })} inputProps={{ min: 0 }} />
            </div>
          )}
        </DialogContent>
        <DialogActions><OutlineBtn onClick={() => setClaimForm(null)} disabled={busy}>Cancel</OutlineBtn><PrimaryBtn onClick={fileClaim} disabled={busy}>Submit claim</PrimaryBtn></DialogActions>
      </Dialog>
    </div>
  );
}

function ReportDialog({ form, setForm, onSubmit, busy }) {
  return (
    <Dialog open={!!form} onClose={() => !busy && setForm(null)} maxWidth="xs" fullWidth>
      <DialogTitle>Report incident</DialogTitle>
      <DialogContent>
        {form && (
          <div style={{ paddingTop: 6 }}>
            <div className="grid grid-cols-2 gap-2" style={{ marginBottom: 12 }}>
              <div><div style={{ fontSize: 12, fontWeight: 600, color: AG.n600 }}>Type</div><select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="ag-input" style={{ marginTop: 4 }}>{TYPES.map((t) => <option key={t}>{t}</option>)}</select></div>
              <div><div style={{ fontSize: 12, fontWeight: 600, color: AG.n600 }}>Severity</div><select value={form.severity} onChange={(e) => setForm({ ...form, severity: e.target.value })} className="ag-input" style={{ marginTop: 4 }}>{SEVERITIES.map((s) => <option key={s}>{s}</option>)}</select></div>
            </div>
            <TextField fullWidth size="small" label="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} sx={{ mb: 1.5 }} />
            <TextField fullWidth size="small" label="What happened?" multiline minRows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} sx={{ mb: 1.5 }} />
            <div className="grid grid-cols-2 gap-2">
              <TextField size="small" label="Location" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} />
              <TextField size="small" type="date" label="Occurred" InputLabelProps={{ shrink: true }} value={form.occurredAt} onChange={(e) => setForm({ ...form, occurredAt: e.target.value })} />
            </div>
          </div>
        )}
      </DialogContent>
      <DialogActions><OutlineBtn onClick={() => setForm(null)} disabled={busy}>Cancel</OutlineBtn><PrimaryBtn onClick={onSubmit} disabled={busy}>Submit</PrimaryBtn></DialogActions>
    </Dialog>
  );
}

function SafetyHR({ showToast }) {
  const [tab, setTab] = useState('Incidents');
  const [data, setData] = useState(null);
  const [active, setActive] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.incidentsAll().then(setData).catch(() => setData({ stats: { total: 0, open: 0, bySeverity: [] }, rows: [] }));
  useEffect(() => { load(); }, []);
  const apply = async (patch) => {
    if (busy) return; setBusy(true);
    try { await api.updateIncident(active.id, patch); showToast?.('Incident updated'); setActive(null); load(); }
    catch (e) { showToast?.(e.message); } finally { setBusy(false); }
  };
  if (!data) return <CenterSpinner />;
  const s = data.stats;
  return (
    <div className="space-y-4 fade-up">
      <SectionTitle title="Health & Safety" sub="Incidents, claims, return-to-work & sustainability" />
      <div className="flex items-center gap-2" style={{ flexWrap: 'wrap' }}>
        {['Incidents', 'Claims', 'Rehab', 'Carbon'].map((t) => <button key={t} type="button" style={tabBtn(tab === t)} onClick={() => setTab(t)}>{t}</button>)}
      </div>
      {tab === 'Claims' && <ClaimsTab showToast={showToast} />}
      {tab === 'Rehab' && <RehabTab showToast={showToast} />}
      {tab === 'Carbon' && <CarbonTab showToast={showToast} />}
      {tab !== 'Incidents' ? null : (<>
      <div className="grid grid-cols-2 lg:grid-cols-6 gap-3">
        <Card style={{ padding: 14 }}><div style={{ fontSize: 22, fontWeight: 700, color: AG.text }}>{s.total}</div><div style={{ fontSize: 12, color: AG.n500 }}>Total</div></Card>
        <Card style={{ padding: 14 }}><div style={{ fontSize: 22, fontWeight: 700, color: AG.text }}>{s.open}</div><div style={{ fontSize: 12, color: AG.n500 }}>Open</div></Card>
        {s.bySeverity.map((b) => <Card key={b.severity} style={{ padding: 14 }}><div style={{ fontSize: 22, fontWeight: 700, color: AG.text }}>{b.count}</div><div style={{ fontSize: 12, color: AG.n500 }}>{b.severity}</div></Card>)}
      </div>
      {data.rows.length === 0 ? <Card><EmptyState icon={ShieldAlert} title="No incidents" desc="Reported incidents appear here to triage." /></Card> : (
        <div style={{ display: 'grid', gap: 8 }}>
          {data.rows.map((i) => (
            <Card key={i.id} style={{ padding: 14, cursor: 'pointer' }} onClick={() => setActive({ ...i, correctiveAction: i.correctiveAction || '' })}>
              <div className="flex items-center justify-between">
                <div>
                  <div style={{ fontWeight: 600, color: AG.text }}>{i.title} <span style={{ color: AG.n400, fontWeight: 400 }}>· {i.type}</span></div>
                  <div style={{ fontSize: 12, color: AG.n500 }}>{i.reporterName} · {i.occurredAt || i.createdAt?.slice(0, 10)}{i.location ? ` · ${i.location}` : ''}</div>
                </div>
                <div className="flex items-center gap-2"><Pill tone={sevTone(i.severity)}>{i.severity}</Pill><Pill tone={statTone(i.status)} dot>{i.status}</Pill></div>
              </div>
            </Card>
          ))}
        </div>
      )}
      <Dialog open={!!active} onClose={() => !busy && setActive(null)} maxWidth="sm" fullWidth>
        {active && (
          <>
            <DialogTitle>{active.title}</DialogTitle>
            <DialogContent>
              <div style={{ fontSize: 12, color: AG.n500, marginBottom: 8 }}>{active.type} · {active.reporterName} · {active.severity}{active.location ? ` · ${active.location}` : ''}</div>
              <div style={{ fontSize: 14, color: AG.text, whiteSpace: 'pre-wrap', marginBottom: 16 }}>{active.description}</div>
              <div style={{ fontSize: 12, fontWeight: 600, color: AG.n600 }}>Severity</div>
              <select value={active.severity} onChange={(e) => setActive({ ...active, severity: e.target.value })} className="ag-input" style={{ marginTop: 4, marginBottom: 12 }}>{SEVERITIES.map((x) => <option key={x}>{x}</option>)}</select>
              <TextField fullWidth size="small" label="Corrective action" multiline minRows={2} value={active.correctiveAction} onChange={(e) => setActive({ ...active, correctiveAction: e.target.value })} />
            </DialogContent>
            <DialogActions style={{ flexWrap: 'wrap', gap: 6 }}>
              <OutlineBtn onClick={() => apply({ severity: active.severity, correctiveAction: active.correctiveAction })} disabled={busy}>Save</OutlineBtn>
              {(NEXT[active.status] || []).map((st) => <PrimaryBtn key={st} onClick={() => apply({ status: st, severity: active.severity, correctiveAction: active.correctiveAction })} disabled={busy}>Mark {st}</PrimaryBtn>)}
            </DialogActions>
          </>
        )}
      </Dialog>
      </>)}
    </div>
  );
}

// Workers' comp / medical claims with an approval lifecycle.
function ClaimsTab({ showToast }) {
  const [data, setData] = useState(null);
  const [active, setActive] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.safetyClaims().then(setData).catch(() => setData({ rows: [], summary: {} }));
  useEffect(() => { load(); }, []);
  const apply = async (patch) => { if (busy) return; setBusy(true); try { await api.updateClaim(active.id, patch); showToast?.('Claim updated'); setActive(null); load(); } catch (e) { showToast?.(e.message); } finally { setBusy(false); } };
  if (!data) return <CenterSpinner />;
  const sum = data.summary || {};
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-3 gap-3">
        <Card style={{ padding: 14 }}><div style={{ fontSize: 20, fontWeight: 700, color: AG.text }}>{sum.count || 0}</div><div style={{ fontSize: 12, color: AG.n500 }}>Claims</div></Card>
        <Card style={{ padding: 14 }}><div style={{ fontSize: 20, fontWeight: 700, color: AG.text }}>{money(sum.totalClaimed)}</div><div style={{ fontSize: 12, color: AG.n500 }}>Claimed</div></Card>
        <Card style={{ padding: 14 }}><div style={{ fontSize: 20, fontWeight: 700, color: AG.successDark }}>{money(sum.totalApproved)}</div><div style={{ fontSize: 12, color: AG.n500 }}>Approved</div></Card>
      </div>
      {data.rows.length === 0 ? <Card><EmptyState icon={ShieldAlert} title="No claims" desc="Compensation and medical claims appear here." /></Card> : (
        <div style={{ display: 'grid', gap: 8 }}>
          {data.rows.map((c) => (
            <Card key={c.id} style={{ padding: 14, cursor: 'pointer' }} onClick={() => setActive({ ...c, amountApproved: c.amountApproved || '', decisionNote: c.decisionNote || '' })}>
              <div className="flex items-center justify-between">
                <div><div style={{ fontWeight: 600, color: AG.text }}>{c.employeeName} <span style={{ color: AG.n400, fontWeight: 400 }}>· {c.type}</span></div><div style={{ fontSize: 12, color: AG.n500 }}>{money(c.amountClaimed)} claimed{c.amountApproved ? ` · ${money(c.amountApproved)} approved` : ''}{c.incidentTitle ? ` · ↳ ${c.incidentTitle}` : ''}</div></div>
                <Pill tone={claimTone(c.status)} dot>{c.status}</Pill>
              </div>
            </Card>
          ))}
        </div>
      )}
      <Dialog open={!!active} onClose={() => setActive(null)} maxWidth="sm" fullWidth>
        {active && (<>
          <DialogTitle>{active.employeeName} · {active.type}</DialogTitle>
          <DialogContent>
            <div style={{ fontSize: 14, color: AG.text, whiteSpace: 'pre-wrap', marginBottom: 14 }}>{active.description}</div>
            <TextField fullWidth size="small" type="number" label="Approved amount" value={active.amountApproved} onChange={(e) => setActive({ ...active, amountApproved: e.target.value })} sx={{ mb: 1.5 }} />
            <TextField fullWidth size="small" label="Decision note" multiline minRows={2} value={active.decisionNote} onChange={(e) => setActive({ ...active, decisionNote: e.target.value })} />
          </DialogContent>
          <DialogActions style={{ flexWrap: 'wrap', gap: 6 }}>
            <OutlineBtn onClick={() => apply({ amountApproved: active.amountApproved === '' ? null : Number(active.amountApproved), decisionNote: active.decisionNote })} disabled={busy}>Save</OutlineBtn>
            {(CLAIM_NEXT[active.status] || []).map((st) => <PrimaryBtn key={st} onClick={() => apply({ status: st, amountApproved: active.amountApproved === '' ? null : Number(active.amountApproved), decisionNote: active.decisionNote })} disabled={busy}>{st}</PrimaryBtn>)}
          </DialogActions>
        </>)}
      </Dialog>
    </div>
  );
}

// Return-to-work / rehabilitation plans.
function RehabTab({ showToast }) {
  const [rows, setRows] = useState(null);
  const [people, setPeople] = useState([]);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.rehabPlans().then(setRows).catch(() => setRows([]));
  useEffect(() => { load(); api.employees().then((r) => setPeople(Array.isArray(r) ? r : r.rows || [])).catch(() => {}); }, []);
  const save = async () => {
    if (!form.employeeId || !form.summary?.trim() || busy) { if (!busy) showToast?.('Employee and summary are required'); return; }
    setBusy(true);
    try { if (form.id) await api.updateRehab(form.id, form); else await api.createRehab(form); setForm(null); load(); } catch (e) { showToast?.(e.message); } finally { setBusy(false); }
  };
  const remove = async (r) => { try { await api.deleteRehab(r.id); load(); } catch (e) { showToast?.(e.message); } };
  if (!rows) return <CenterSpinner />;
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-end"><PrimaryBtn onClick={() => setForm({ employeeId: '', summary: '', restrictions: '', startDate: '', expectedReturnDate: '', status: 'Active' })}><Plus size={14} style={{ marginRight: 6 }} /> New plan</PrimaryBtn></div>
      {rows.length === 0 ? <Card><EmptyState icon={HardHat} title="No rehab plans" desc="Return-to-work plans appear here." /></Card> : (
        <div style={{ display: 'grid', gap: 8 }}>
          {rows.map((r) => (
            <Card key={r.id} style={{ padding: 14 }}>
              <div className="flex items-start justify-between" style={{ gap: 8 }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 600, color: AG.text }}>{r.employeeName} <Pill tone={r.status === 'Completed' ? 'success' : r.status === 'OnHold' ? 'warning' : 'info'}>{r.status}</Pill></div>
                  <div style={{ fontSize: 13, color: AG.n600, marginTop: 4 }}>{r.summary}</div>
                  {r.restrictions && <div style={{ fontSize: 12, color: AG.n500, marginTop: 2 }}>Restrictions: {r.restrictions}</div>}
                  <div style={{ fontSize: 12, color: AG.n400, marginTop: 2 }}>{r.startDate || '—'} → {r.actualReturnDate || r.expectedReturnDate || '—'}</div>
                </div>
                <div className="flex items-center gap-1">
                  <button onClick={() => setForm({ id: r.id, employeeId: r.employeeId, summary: r.summary, restrictions: r.restrictions || '', startDate: r.startDate || '', expectedReturnDate: r.expectedReturnDate || '', actualReturnDate: r.actualReturnDate || '', status: r.status })} style={{ background: 'none', border: 'none', cursor: 'pointer', color: AG.n400, fontSize: 12, fontWeight: 600 }}>Edit</button>
                  <button onClick={() => remove(r)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: AG.n400 }}><Trash2 size={15} /></button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
      <Dialog open={!!form} onClose={() => setForm(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{form?.id ? 'Edit plan' : 'New rehab plan'}</DialogTitle>
        <DialogContent>
          {form && (
            <div style={{ paddingTop: 6, display: 'grid', gap: 12 }}>
              {!form.id && <label><span style={{ display: 'block', fontSize: 12, fontWeight: 600, color: AG.n600, marginBottom: 4 }}>Employee</span><select className="ag-input" value={form.employeeId} onChange={(e) => setForm({ ...form, employeeId: e.target.value })}><option value="">Select…</option>{people.map((p) => <option key={p.id} value={p.id}>{p.name} · {p.dept}</option>)}</select></label>}
              <TextField size="small" label="Plan summary" multiline minRows={2} value={form.summary} onChange={(e) => setForm({ ...form, summary: e.target.value })} />
              <TextField size="small" label="Restrictions" value={form.restrictions} onChange={(e) => setForm({ ...form, restrictions: e.target.value })} />
              <div className="flex gap-2">
                <TextField size="small" type="date" label="Start" InputLabelProps={{ shrink: true }} value={form.startDate} onChange={(e) => setForm({ ...form, startDate: e.target.value })} sx={{ flex: 1 }} />
                <TextField size="small" type="date" label="Expected return" InputLabelProps={{ shrink: true }} value={form.expectedReturnDate} onChange={(e) => setForm({ ...form, expectedReturnDate: e.target.value })} sx={{ flex: 1 }} />
              </div>
              {form.id && (<div className="flex gap-2">
                <TextField size="small" type="date" label="Actual return" InputLabelProps={{ shrink: true }} value={form.actualReturnDate} onChange={(e) => setForm({ ...form, actualReturnDate: e.target.value })} sx={{ flex: 1 }} />
                <label style={{ flex: 1 }}><span style={{ display: 'block', fontSize: 12, fontWeight: 600, color: AG.n600, marginBottom: 4 }}>Status</span><select className="ag-input" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>{['Active', 'OnHold', 'Completed'].map((x) => <option key={x}>{x}</option>)}</select></label>
              </div>)}
            </div>
          )}
        </DialogContent>
        <DialogActions><OutlineBtn onClick={() => setForm(null)} disabled={busy}>Cancel</OutlineBtn><PrimaryBtn onClick={save} disabled={busy}>Save</PrimaryBtn></DialogActions>
      </Dialog>
    </div>
  );
}

// Carbon footprint log + sustainability summary.
function CarbonTab({ showToast }) {
  const [data, setData] = useState(null);
  const [factors, setFactors] = useState({ factors: {}, categories: [] });
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.carbonEntries().then(setData).catch(() => setData({ rows: [], summary: {} }));
  useEffect(() => { load(); api.carbonFactors().then(setFactors).catch(() => {}); }, []);
  const save = async () => {
    if (!form.period || !form.category || !(Number(form.activityValue) > 0) || busy) { if (!busy) showToast?.('Period, category and a positive amount are required'); return; }
    setBusy(true);
    try { await api.addCarbon({ ...form, activityValue: Number(form.activityValue) }); setForm(null); load(); } catch (e) { showToast?.(e.message); } finally { setBusy(false); }
  };
  const remove = async (e) => { try { await api.deleteCarbon(e.id); load(); } catch (err) { showToast?.(err.message); } };
  if (!data) return <CenterSpinner />;
  const sum = data.summary || {};
  const thisMonth = new Date().toISOString().slice(0, 7);
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <Card style={{ padding: 14 }}><div className="flex items-center gap-2"><Leaf size={18} style={{ color: AG.successDark }} /><div style={{ fontSize: 20, fontWeight: 700, color: AG.text }}>{sum.totalTonnes || 0} t</div></div><div style={{ fontSize: 12, color: AG.n500 }}>Total CO₂e ({sum.entryCount || 0} entries)</div></Card>
        <Card style={{ padding: 14 }}><div style={{ fontSize: 13, color: AG.n600 }}>{Object.entries(sum.byCategory || {}).slice(0, 4).map(([k, v]) => <div key={k} className="flex justify-between"><span>{k}</span><span style={{ fontWeight: 600 }}>{v} kg</span></div>)}</div></Card>
      </div>
      <div className="flex items-center justify-end"><PrimaryBtn onClick={() => setForm({ period: thisMonth, category: factors.categories[0] || 'Electricity', activityValue: '', note: '' })}><Plus size={14} style={{ marginRight: 6 }} /> Log activity</PrimaryBtn></div>
      {data.rows.length === 0 ? <Card><EmptyState icon={Leaf} title="No entries" desc="Log energy, fuel and travel to track your footprint." /></Card> : (
        <Card style={{ padding: 0, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13.5 }}>
            <thead><tr style={{ background: AG.n50, color: AG.n600, textAlign: 'left' }}>{['Period', 'Category', 'Activity', 'kg CO₂e', ''].map((h) => <th key={h} style={{ padding: '10px 14px', fontWeight: 600 }}>{h}</th>)}</tr></thead>
            <tbody>
              {data.rows.map((e) => (
                <tr key={e.id} style={{ borderTop: `1px solid ${AG.divider}` }}>
                  <td style={{ padding: '10px 14px' }}>{e.period}</td>
                  <td style={{ padding: '10px 14px' }}>{e.category}</td>
                  <td style={{ padding: '10px 14px' }}>{e.activityValue} {e.unit}</td>
                  <td style={{ padding: '10px 14px', fontWeight: 600 }}>{e.co2eKg}</td>
                  <td style={{ padding: '10px 14px', textAlign: 'right' }}><button onClick={() => remove(e)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: AG.n400 }}><Trash2 size={15} /></button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
      <Dialog open={!!form} onClose={() => setForm(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Log activity</DialogTitle>
        <DialogContent>
          {form && (
            <div style={{ paddingTop: 6, display: 'grid', gap: 12 }}>
              <div className="flex gap-2">
                <TextField size="small" type="month" label="Period" InputLabelProps={{ shrink: true }} value={form.period} onChange={(e) => setForm({ ...form, period: e.target.value })} sx={{ flex: 1 }} />
                <label style={{ flex: 1 }}><span style={{ display: 'block', fontSize: 12, fontWeight: 600, color: AG.n600, marginBottom: 4 }}>Category</span><select className="ag-input" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>{factors.categories.map((c) => <option key={c} value={c}>{c}</option>)}</select></label>
              </div>
              <TextField size="small" type="number" label={`Amount (${factors.factors[form.category]?.unit || 'unit'})`} value={form.activityValue} onChange={(e) => setForm({ ...form, activityValue: e.target.value })} inputProps={{ min: 0, step: 'any' }} />
              <p style={{ fontSize: 12.5, color: AG.n500, margin: 0 }}>≈ {Math.round((Number(form.activityValue) || 0) * (factors.factors[form.category]?.factor || 0) * 100) / 100} kg CO₂e</p>
              <TextField size="small" label="Note (optional)" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} />
            </div>
          )}
        </DialogContent>
        <DialogActions><OutlineBtn onClick={() => setForm(null)} disabled={busy}>Cancel</OutlineBtn><PrimaryBtn onClick={save} disabled={busy}>Save</PrimaryBtn></DialogActions>
      </Dialog>
    </div>
  );
}
